import React, { useCallback, useMemo, useState } from 'react';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import ProductModel from './models/ProductModel';
import ProductModal from './ProductModal';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { logAuditEvent } from '../../../utils/auditLogger';
import { buildProductSavePayload, mapProductFromApiAsync } from './utils/mapProduct';
import { toBooleanFromBit } from './utils/productHelpers';
import { validateProduct } from './productValidation';

function ProductGrid({ ProductData, fetchProducts, UserPermissions = {}, moduleName = 'Product' }) {
  const [actionType, setActionType] = useState();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [productModel, setProductModel] = useState(new ProductModel());
  const [filterText, setFilterText] = useState('');
  const [alert, setAlert] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const isRowActive = (row) => toBooleanFromBit(row?.IsActive, false);
  const isPromoBanner = (row) => toBooleanFromBit(row?.IsPromoBanner, false);

  const closeModal = () => {
    setIsOpen(false);
    fetchProducts();
  };

  const rows = Array.isArray(ProductData)
    ? ProductData.filter((item) => {
        const search = filterText.toLowerCase();
        return (
          (item.ProductName && item.ProductName.toLowerCase().includes(search)) ||
          (item.CategoryName && item.CategoryName.toLowerCase().includes(search)) ||
          (item.SubCategoryName && item.SubCategoryName.toLowerCase().includes(search)) ||
          (item.Slug && item.Slug.toLowerCase().includes(search)) ||
          (item.VendorName && item.VendorName.toLowerCase().includes(search))
        );
      })
    : [];

  const loadProductDetail = async (productId) => {
    const response = await axios.get(`${getApiBaseUrl()}/ec-product/getById`, {
      params: { ProductId: productId },
    });
    return mapProductFromApiAsync(response.data?.data);
  };

  const openActionType = useCallback(async (row, name) => {
    const detail = row?.ProductName ? `Product: ${row.ProductName}` : '';
    logAuditEvent(moduleName, `${name} Click`, `${name} clicked. ${detail}`.trim());
    setLoadingDetail(true);
    try {
      const full = row?.ProductId ? await loadProductDetail(row.ProductId) : row;
      setProductModel(full || new ProductModel());
      setActionType(name);
      setIsOpen(true);
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to load product details', 'Error', setAlert);
    } finally {
      setLoadingDetail(false);
    }
  }, [moduleName]);

  const toggleActive = useCallback(async (row) => {
    if (UserPermissions.EditModule !== 1 || !row?.ProductId) return;

    const nextActive = !isRowActive(row);
    setTogglingId(row.ProductId);
    logAuditEvent(
      moduleName,
      'Toggle Active',
      `Product ${row.ProductName} set to ${nextActive ? 'active' : 'inactive'}`
    );

    try {
      const payload = buildProductSavePayload(
        { ...row, IsActive: nextActive },
        getStorageValues('userId')
      );
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/save`, payload);
      const result = response.data?.data;
      if (result?.success === 1) {
        CreateAlert(
          result.message || `Product ${nextActive ? 'activated' : 'deactivated'} successfully!!`,
          'Success',
          setAlert
        );
        fetchProducts();
      } else {
        CreateAlert(result?.message || 'Failed to update product status!!', 'Warning', setAlert);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to update product status!!', 'Error', setAlert);
    } finally {
      setTogglingId(null);
    }
  }, [UserPermissions.EditModule, fetchProducts, moduleName]);

  const onSubmit = async (e, data) => {
    if (e.target.name === 'Edit') {
      const payload = buildProductSavePayload(data, getStorageValues('userId'));
      if (!validateProduct(payload, setAlert)) {
        return { success: false };
      }
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-product/save`, payload);
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Product updated successfully!!', 'Success', setAlert);
          closeModal();
          return { success: true, productId: result.ProductId || data.ProductId };
        }
        CreateAlert(result?.message || 'Failed to update product!!', 'Warning', setAlert);
        return { success: false };
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to update product!!', 'Error', setAlert);
        return { success: false };
      }
    } else if (e.target.name === 'Delete') {
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-product/delete`, {
          ProductId: productModel.ProductId,
          UserId: getStorageValues('userId'),
        });
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Product deleted successfully!!', 'Success', setAlert);
          fetchProducts();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to delete product!!', 'Error', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to delete product!!', 'Error', setAlert);
      }
    }
    return { success: false };
  };

  const columns = useMemo(() => [
    {
      id: 'ProductId',
      name: 'Entry',
      selector: (row) => row.ProductId,
      sortable: true,
      omit: true,
    },
    {
      name: <div className="titlename"><span>Product</span></div>,
      selector: (row) => row.ProductName,
      cell: (row) => <span title={row.ProductName}>{row.ProductName}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: <div className="titlename"><span>Category</span></div>,
      selector: (row) => row.CategoryName,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: <div className="titlename"><span>Sub Category</span></div>,
      selector: (row) => row.SubCategoryName,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: <div className="titlename"><span>Price</span></div>,
      selector: (row) => row.Price,
      sortable: true,
      sortIcon: <SortArrow />,
      width: '110px',
    },
    {
      name: <div className="titlename"><span>Active</span></div>,
      cell: (row) => {
        const active = isRowActive(row);
        const toggleId = `product-active-${row.ProductId}`;
        if (UserPermissions.EditModule === 1) {
          return (
            <div className="status-toggle modal-status">
              <input
                type="checkbox"
                id={toggleId}
                className="check"
                checked={active}
                disabled={togglingId === row.ProductId}
                onChange={() => toggleActive(row)}
              />
              <label htmlFor={toggleId} className="checktoggle" />
            </div>
          );
        }
        return (
          <span className={`badge ${active ? 'bg-success' : 'bg-secondary'}`}>
            {active ? 'Yes' : 'No'}
          </span>
        );
      },
      width: '110px',
      style: { justifyContent: 'center' },
    },
    {
      name: <div className="titlename"><span>Promo Banner</span></div>,
      selector: (row) => (isPromoBanner(row) ? 'Yes' : 'No'),
      cell: (row) => {
        const promo = isPromoBanner(row);
        return (
          <span className={`badge ${promo ? 'bg-primary' : 'bg-secondary'}`}>
            {promo ? 'Yes' : 'No'}
          </span>
        );
      },
      sortable: true,
      sortIcon: <SortArrow />,
      width: '120px',
      style: { justifyContent: 'center' },
    },
  ], [UserPermissions.EditModule, togglingId, toggleActive]);

  const tableColumns = useMemo(() => {
    const cols = [...columns];
    if (UserPermissions.ViewModule === 1 || UserPermissions.EditModule === 1 || UserPermissions.DeleteModule === 1) {
    cols.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          {UserPermissions.ViewModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={(e) => { e.preventDefault(); openActionType(row, 'View'); }}>
              <Icons.Eye className="action-eye" />
            </Link>
          ) : null}
          {UserPermissions.EditModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={(e) => { e.preventDefault(); openActionType(row, 'Edit'); }}>
              <Icons.Edit className="feather-edit" />
            </Link>
          ) : null}
          {UserPermissions.DeleteModule === 1 ? (
            <Link className="confirm-text p-2" to="" onClick={(e) => { e.preventDefault(); openActionType(row, 'Delete'); }}>
              <Icons.Trash2 className="feather-trash-2" />
            </Link>
          ) : null}
        </div>
      ),
      style: { justifyContent: 'center' },
      width: '150px',
    });
    }
    return cols;
  }, [columns, UserPermissions.ViewModule, UserPermissions.EditModule, UserPermissions.DeleteModule, openActionType]);

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          {loadingDetail ? <div className="p-2 text-muted small">Loading...</div> : null}
          <DataTable
            className="table-responsive product-list"
            columns={tableColumns}
            data={rows}
            pagination
            defaultSortField="ProductId"
            defaultSortAsc={false}
            sortIcon={<SortArrow />}
            persistTableHead
          />
        </div>
      </div>
      <ProductModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={productModel}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ProductGrid;
